import { errorCodeOf, errorDetailsOf } from "@luckymenu/api-client";
import { useQuery, useTRPC } from "@luckymenu/api-client/react";
import type { CurrentMembership, MyRestaurant } from "@luckymenu/core";
import { useEffect } from "react";
import { useActiveRestaurant, useSupportSession, useSwitchRestaurant } from "../provider";

export type RestaurantSession =
	| { status: "loading" }
	| { status: "error"; error: unknown; retry: () => void }
	/** Belongs to no restaurant: show "No restaurant yet" and the join form. */
	| { status: "none" }
	/** Belongs to several and has not chosen: show the chooser. */
	| { status: "choose"; restaurants: MyRestaurant[] }
	/** A support session that has expired or was refused. */
	| { status: "supportEnded" }
	| { status: "ready"; restaurants: MyRestaurant[]; current: CurrentMembership };

/**
 * Which restaurant the staff member works in. One membership is chosen
 * automatically; with several, the stored choice is kept while it is still
 * valid. A removed or disabled membership sends the person back to the
 * chooser. In an admin support session the restaurant comes from the session
 * and no membership is needed. Both staff apps render their gate from this.
 */
export function useRestaurantSession(): RestaurantSession {
	const trpc = useTRPC();
	const switchRestaurant = useSwitchRestaurant();
	const support = useSupportSession((s) => s.session);
	const restaurantId = useActiveRestaurant((s) => s.restaurantId);
	const hydrated = useActiveRestaurant((s) => s.hydrated);
	const list = useQuery({
		...trpc.staff.tenancy.listMyRestaurants.queryOptions(),
		enabled: support === null,
	});
	const restaurants = list.data;

	const stored = restaurants?.find((r) => r.id === restaurantId);
	const only = restaurants?.length === 1 ? restaurants[0] : undefined;
	const chosen = stored ?? only ?? null;

	// Keep the store in step: pick the only restaurant, forget one that is gone.
	useEffect(() => {
		if (support || !hydrated || !restaurants) return;
		const next = chosen?.id ?? null;
		if (next !== restaurantId) void switchRestaurant(next);
	}, [support, hydrated, restaurants, chosen, restaurantId, switchRestaurant]);

	const active = support !== null || (chosen !== null && chosen.id === restaurantId);
	const current = useQuery({
		...trpc.staff.tenancy.getCurrent.queryOptions(),
		enabled: active,
	});

	// Removed or disabled since the list was loaded: reload the list and choose again.
	const currentCode = current.error ? errorCodeOf(current.error) : null;
	const lostAccess = !support && (currentCode === "NOT_FOUND" || currentCode === "FORBIDDEN");
	useEffect(() => {
		if (lostAccess) {
			void switchRestaurant(null).then(() => list.refetch());
		}
	}, [lostAccess, switchRestaurant, list]);

	if (support) {
		if (current.isPending) return { status: "loading" };
		if (current.error) {
			const ended =
				currentCode === "NOT_FOUND" ||
				(currentCode === "FORBIDDEN" && errorDetailsOf(current.error).reason === "support_session");
			return ended
				? { status: "supportEnded" }
				: { status: "error", error: current.error, retry: () => void current.refetch() };
		}
		return { status: "ready", restaurants: [], current: current.data };
	}
	if (!hydrated || list.isPending) return { status: "loading" };
	if (list.error) return { status: "error", error: list.error, retry: () => void list.refetch() };
	if (!restaurants || restaurants.length === 0) return { status: "none" };
	if (!chosen) return { status: "choose", restaurants };
	if (!active || current.isPending || lostAccess) return { status: "loading" };
	if (current.error) {
		return { status: "error", error: current.error, retry: () => void current.refetch() };
	}
	return { status: "ready", restaurants, current: current.data };
}
